import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { isMobile } from 'react-device-detect'
import styles from './video-button.module.scss'
import cn from 'classnames'
import WithTooltip from 'components/shared/WithTooltip'

const Video = props => {
    const { videoSourceRef, spinnerRef, downloadRef, videoTagRef } = props.refObj

    const ref = useRef(null)
    const [videoError, setVideoError] = useState(false)
    const [retryCount, setRetryCount] = useState(0)
    const [thumbnailError, setThumbnailError] = useState(false)

    // Find matching BSV attachment - prefer mp4_recording if available
    const matchingBsvAttachment = useMemo(() => {
        const { answer } = props
        if (!answer?.bsv_attachments || !answer?.recording_info?.attachment_id) return null

        // Find attachment matching the recording_info.attachment_id
        return answer.bsv_attachments.find(
            att => att.attachment_id === answer.recording_info.attachment_id,
        )
    }, [props.answer?.bsv_attachments, props.answer?.recording_info?.attachment_id])

    // Render BSV attachment status
    const renderBsvAttachmentStatus = useCallback(matchingBsvAttachment => {
        const bsvTx = matchingBsvAttachment?.bsv_tx
        const txId = bsvTx?.tx_id

        // Check if we have a pending transaction that's more than 5 seconds old
        const isAwaitingResubmission =
            bsvTx && !txId && (new Date() - new Date(bsvTx.created_at)) / 1000 > 5

        // Show different status for old pending transactions
        if (isAwaitingResubmission) {
            const retryInfo = bsvTx.retries > 0 ? ` (${bsvTx.retries} retries)` : ''
            return (
                <WithTooltip
                    tip={`Treechat wasn't able to post this onchain immediately but will try again on the next block ${retryInfo}`}
                >
                    <i className="fa fa-clock-o" />
                </WithTooltip>
            )
        }

        // Show spinner for new pending transactions
        if (!txId) {
            return (
                <WithTooltip tip="Transaction pending">
                    <i className="fa fa-spinner fa-spin" />
                </WithTooltip>
            )
        }

        const isUnconfirmed = !bsvTx?.confirmed

        return (
            <WithTooltip tip={`${isUnconfirmed ? 'Unconfirmed' : 'Confirmed'} tx: ${txId}`}>
                <a
                    href={`https://whatsonchain.com/tx/${txId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={ev => ev.stopPropagation()}
                >
                    <i className={`fa fa-cube`} />
                </a>
            </WithTooltip>
        )
    }, [])

    const resetVideoTime = e => {
        e.target.currentTime = 0
    }

    // Handle video loading errors with fast, consistent retry logic
    const handleVideoError = useCallback(
        e => {
            console.log('>>> Video load error, retry count:', retryCount)
            if (retryCount < 3) {
                // Retry up to 3 times with consistent 1s delays (no exponential backoff)
                const delay = 1000 // Always 1s - fast and consistent
                setTimeout(() => {
                    setRetryCount(prev => prev + 1)
                    // Force video reload by updating the src
                    if (e.target) {
                        const currentSrc = e.target.currentSrc
                        e.target.load() // Reload the video
                    }
                }, delay)
            } else {
                setVideoError(true)
                console.log('>>> Video failed to load after 3 retries')
            }
        },
        [retryCount],
    )

    // Reset error state when answer changes (new video)
    useEffect(() => {
        setVideoError(false)
        setRetryCount(0)
        setThumbnailError(false)
    }, [
        props.answer?.mp4_recording_url,
        props.answer?.recording_url,
        props.answer?.recording_thumbnail_url,
    ])

    // We use styles instead of a boolean bc there are situations where we have to use the DOM to display the link from Answer.showDownload.
    // editedVideoUrl will be overridden after saving via Answer.showDownload.
    const isMp4 =
        props.answer.mp4_recording_url || props.answer.original_recording_type === 'video/mp4'
    const justEdited = props.editedVideoUrl && !props.editMode
    const showSpinner = !isMp4 || justEdited
    //console.log('show spinner', showSpinner)
    const spinnerStyles = showSpinner ? { display: 'block' } : { display: 'none' }

    const showDownload = isMobile && isMp4 && !props.editedVideoUrl
    const downloadStyles = showDownload ? { display: 'block' } : { display: 'none' }

    let arr, seoKey
    if (showDownload && props.answer.path) {
        arr = props.answer.path.split('/')
        seoKey = arr[arr.length - 1]
    }

    const [isPlaying, setIsPlaying] = useState(false)

    // Create thumbnail URL with cache-busting if there was an error
    const thumbnailUrl = useMemo(() => {
        let url = props.answer?.recording_thumbnail_url
        if (!url) return null

        if (thumbnailError) {
            // Add cache-busting parameter similar to AnswerImage component
            const separator = url.includes('?') ? '&' : '?'
            url = `${url}${separator}break_cache=true&retry=${Date.now()}`
        }
        return url
    }, [props.answer?.recording_thumbnail_url, thumbnailError])

    const initFrame = '1'
    //console.log('render video', props)
    return (
        <div className="video-comp" ref={ref} onClick={e => e.stopPropagation()}>
            {props.answer.mp4_recording_url ? (
                <link rel="preload" as="video" href={props.answer.mp4_recording_url} />
            ) : (
                <link rel="preload" as="video" href={props.answer.recording_url} />
            )}

            {props?.answer?.recording_thumbnail_url && !isPlaying && (
                <>
                    <link rel="preload" as="image" href={thumbnailUrl} />

                    <div
                        className={cn(styles.videoContainer)}
                        style={{
                            aspectRatio:
                                props.answer?.video_width && props.answer?.video_height
                                    ? `${props.answer.video_width} / ${props.answer.video_height}`
                                    : undefined,
                            position: 'relative',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {matchingBsvAttachment && (
                            <div className={styles.bsvStatusOverlay}>
                                {renderBsvAttachmentStatus(matchingBsvAttachment)}
                            </div>
                        )}

                        <img
                            src={thumbnailUrl}
                            width={props.answer?.video_width}
                            height={props.answer?.video_height}
                            onError={e => {
                                setThumbnailError(true)
                            }}
                            style={{
                                aspectRatio:
                                    props.answer?.video_width && props.answer?.video_height
                                        ? `${props.answer.video_width} / ${props.answer.video_height}`
                                        : undefined,
                            }}
                        />
                        <div
                            onClick={e => {
                                e.stopPropagation()
                                setIsPlaying(true)
                            }}
                            className={cn(styles.playButtonContainer)}
                        >
                            <div className={styles.playButton}>
                                <i className="fa fa-play"></i>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {(isPlaying || !props?.answer?.recording_thumbnail_url) && (
                <div
                    className={cn(styles.videoContainer)}
                    style={{
                        aspectRatio:
                            props.answer?.video_width && props.answer?.video_height
                                ? `${props.answer.video_width} / ${props.answer.video_height}`
                                : undefined,
                        position: 'relative',
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    {matchingBsvAttachment && (
                        <div className={styles.bsvStatusOverlay}>
                            {renderBsvAttachmentStatus(matchingBsvAttachment)}
                        </div>
                    )}

                    {videoError && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                background: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                padding: '10px',
                                borderRadius: '5px',
                                textAlign: 'center',
                                zIndex: 10,
                            }}
                        >
                            <i
                                className="fa fa-exclamation-triangle"
                                style={{ marginRight: '5px' }}
                            />
                            Video temporarily unavailable
                        </div>
                    )}

                    {retryCount > 0 && !videoError && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                background: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                padding: '5px 10px',
                                borderRadius: '3px',
                                fontSize: '12px',
                                zIndex: 10,
                            }}
                        >
                            <i className="fa fa-spinner fa-spin" style={{ marginRight: '5px' }} />
                            Retrying...
                        </div>
                    )}

                    <video
                        autoPlay={isPlaying}
                        width={props.answer?.video_width}
                        height={props.answer?.video_height}
                        onLoadedMetadata={resetVideoTime}
                        onError={handleVideoError}
                        controls
                        playsInline
                        preload="metadata"
                        ref={videoTagRef}
                        style={{
                            width: '100%',
                            aspectRatio:
                                props.answer?.video_width && props.answer?.video_height
                                    ? `${props.answer.video_width} / ${props.answer.video_height}`
                                    : undefined,
                            display: 'block',
                            ...(!isMobile ? { borderRadius: '5px' } : {}),
                        }}
                        allowFullScreen
                        webkitallowfullscreen="true"
                        mozallowfullscreen="true"
                    >
                        {props.answer.mp4_recording_url && (
                            <source src={`${props.answer.mp4_recording_url}`} type="video/mp4" />
                        )}
                        <source
                            ref={videoSourceRef}
                            src={`${props.answer.recording_url}#t=${initFrame}`}
                            type={props.answer.original_recording_type}
                        />
                    </video>
                </div>
            )}

            {false &&
                gon.currentUser &&
                props.answer.user_id === gon.currentUser.id &&
                !props.isTitle && (
                    <div className="video-controls">
                        <div style={spinnerStyles} className="process-video" ref={spinnerRef}>
                            <div>
                                <i className="fa fa-circle-o-notch fa-spin" />
                                <span>Processing</span>
                            </div>
                        </div>

                        <div style={downloadStyles} className="download-btn" ref={downloadRef}>
                            <a href={props.answer.mp4_recording_url} download={seoKey}>
                                Download
                            </a>
                        </div>
                    </div>
                )}
        </div>
    )
}

// no video
// edit

// video
// edit
// edit
// unprocessed
// processed

const prevKeys = {}

const areEqual = (prevProps, nextProps) => {
    if (prevProps.editMode !== nextProps.editMode) return false

    const nextAnswer = nextProps.answer
    const prevAnswer = prevProps.answer

    let equal = true

    // mark as not equal so we update the ui when the bsv attachment status changes
    if (
        JSON.stringify(nextAnswer?.bsv_attachments) !== JSON.stringify(prevAnswer?.bsv_attachments)
    ) {
        return false
    }

    if (nextProps.editedVideoUrl) {
        // We're editing and ...
        if (
            nextAnswer.mp4_recording_url &&
            nextAnswer.mp4_recording_url !== prevAnswer.mp4_recording_url
        ) {
            // This overrides url link if an updated processed vid comes back.
            //console.log('edited mp4 show download')
            // Update via DOM so no need to set equal to false.
            nextProps.showDownload(nextAnswer)
        } else if (nextProps.editedVideoUrl !== prevProps.editedVideoUrl) {
            // This means we updated the edited video.
            //console.log('edited url')
            equal = false
            prevKeys[nextAnswer.id] = nextProps.editedVideoUrl
        }
    } else if (
        nextAnswer.mp4_recording_url &&
        nextAnswer.mp4_recording_url !== prevAnswer.mp4_recording_url
    ) {
        // Not editing, but updated url comes back, perhaps from a newly created video or a different machine?
        // TODO might want to add mp4_recording_key to cached answers for consistency, using url for now.
        //console.log('mp4 show download')
        // Update via DOM so no need to set equal to false.
        nextProps.showDownload(nextAnswer)
    } else if (nextAnswer.recording_key && nextAnswer.recording_key !== prevAnswer.recording_key) {
        equal = false
    } else if (nextAnswer.recording_thumbnail_url !== prevAnswer.recording_thumbnail_url) {
        // Re-render when thumbnail URL changes (becomes available or updates)
        equal = false
    }
    //console.log('equal', equal)
    return equal
}

export default React.memo(Video, areEqual)
